/**
 * Admin console date-time in a fixed IANA zone: YYYY/MM/DD HH:mm (24h).
 * Returns "—" for missing or invalid values.
 */
function formatAdminDateTimeInTimeZone(
	value: string | null | undefined,
	timeZone: string,
): string {
	if (!value) return "—";
	const date = new Date(value);
	if (!Number.isFinite(date.getTime())) return "—";

	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
	}).formatToParts(date);

	const get = (type: Intl.DateTimeFormatPartTypes) =>
		parts.find((part) => part.type === type)?.value;

	const year = get("year");
	const month = get("month");
	const day = get("day");
	const hour = get("hour");
	const minute = get("minute");
	if (!year || !month || !day || !hour || !minute) return "—";

	return `${year}/${month}/${day} ${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
}

/** Admin console date display: YYYY/MM/DD HH:mm (24h, browser local timezone). */
export function formatAdminDateTime(value: string | null | undefined): string {
	if (!value) return "—";
	const date = new Date(value);
	if (!Number.isFinite(date.getTime())) return "—";

	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	const hh = String(date.getHours()).padStart(2, "0");
	const mm = String(date.getMinutes()).padStart(2, "0");
	return `${y}/${m}/${d} ${hh}:${mm}`;
}

/**
 * Admin console date-time in Asia/Tokyo (JST): YYYY/MM/DD HH:mm.
 * Independent of the browser machine timezone.
 */
export function formatAdminDateTimeJst(
	value: string | null | undefined,
): string {
	return formatAdminDateTimeInTimeZone(value, "Asia/Tokyo");
}

/** Admin console date-only display: YYYY/MM/DD. */
export function formatAdminDate(value: string | null | undefined): string {
	if (!value) return "—";
	const date = new Date(value);
	if (!Number.isFinite(date.getTime())) return "—";

	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}/${m}/${d}`;
}
